import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";

import {
  Loader,
  Tab,
  TabHeader,
  TabHeaders,
  Tabs,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import { useCurrentUser } from "@hooks/queries";
import { useApiRequest } from "@lib/hooks";
import { useProducts } from "@queries";
import { useContainer } from "@src/contexts/container-context";
import { ZeroStateMessage } from "@src/views/shared";

import { createCurriculumResourcesTracking } from "./tracking";
import { PRODUCT_TABS, getVisibleTabs } from "./utils";
import { CurriculumResourcesProduct } from "./views";

import "./index.scss";

const CurriculumResources = () => {
  const { documentsService } = useContainer();
  const { organizationCode } = useCurrentUser();

  const products = useProducts();

  const tracking = useMemo(
    () =>
      new Map(
        PRODUCT_TABS.map((tab) => [
          tab.productId,
          createCurriculumResourcesTracking(tab.productId, tab.label),
        ])
      ),
    []
  );

  const [selectedTab, setSelectedTab] = useState();

  const { loading: loadingCurriculumResources, response: documentResponses } =
    useApiRequest(async () => {
      const productIds = PRODUCT_TABS.map((tab) => tab.productId).sort();
      const responses = await Promise.allSettled(
        productIds.map((productId) => {
          const params = {
            productId,
            organizationCode:
              productId === Product.PROG ? organizationCode : undefined,
          };
          return documentsService.getDocuments(params);
        })
      );
      return new Map(
        productIds.map((productId, index) => [
          productId,
          responses[index].value,
        ])
      );
    });

  const visibleTabs = useMemo(
    () =>
      getVisibleTabs(
        products,
        (productId) => documentResponses?.get(productId)?.length ?? 0
      ),
    [products, documentResponses]
  );

  const curriculumResources =
    documentResponses?.get(
      PRODUCT_TABS.find((tab) => tab.name === selectedTab)?.productId
    ) ?? [];

  useEffect(() => {
    if (documentResponses) {
      const tab = visibleTabs[0];
      if (tab) {
        setSelectedTab(tab.name);
        tracking.get(tab.productId)?.trackSelectTabEvent();
      }
    }
  }, [documentResponses, visibleTabs, tracking]);

  const onTabClick = (tabName) => {
    const tab = PRODUCT_TABS.find((t) => t.name === tabName);
    setSelectedTab(tab.name);
    tracking.get(tab.productId)?.trackSelectTabEvent();
  };

  const pageHeader = (
    <PageHeader
      title="Curriculum Resources"
      subTitle="Lessons plans and assessment tools designed to support your training programs and enhance learning outcomes."
    />
  );

  const renderContent = () => {
    if (loadingCurriculumResources) {
      return <Loader fixed overlay />;
    }

    if (visibleTabs.length === 0) {
      return (
        <div className="curriculum-resources-product">
          <ZeroStateMessage
            message="No resources found."
            icon={<FontAwesomeIcon icon={["fa-solid", "ban"]} />}
          />
        </div>
      );
    }

    return (
      <Tabs selectedTabName={selectedTab} staticHeaders>
        <TabHeaders className="curriculum-resources-tabs-headers">
          {visibleTabs.map((tab) => (
            <TabHeader key={tab.name} name={tab.name} onTabClick={onTabClick}>
              {tab.label}
            </TabHeader>
          ))}
        </TabHeaders>
        {visibleTabs.map((tab) => (
          <Tab key={tab.name} name={tab.name}>
            <CurriculumResourcesProduct
              data={curriculumResources}
              loading={loadingCurriculumResources}
              product={tab}
              tracking={tracking.get(tab.productId)}
              organizationCode={organizationCode}
            />
          </Tab>
        ))}
      </Tabs>
    );
  };

  return (
    <PageLayout header={pageHeader} className="curriculum-resources-page">
      {renderContent()}
    </PageLayout>
  );
};

export default CurriculumResources;
